import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { authClient } from "@/lib/auth";
import { migrateLocalCompletions } from "@/lib/progress.functions";

async function runOneTimeCompletionMigration() {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem("wk-migrated-completions") === "1") return;
    const raw = window.localStorage.getItem("wk-completed-lessons");
    const arr = raw ? JSON.parse(raw) : [];
    if (Array.isArray(arr) && arr.length > 0) {
      const lessonIds = arr.filter(
        (x): x is string => typeof x === "string" && /^\d+\.\d+$/.test(x),
      );
      if (lessonIds.length > 0) {
        await migrateLocalCompletions({ data: { lessonIds } });
      }
    }
    window.localStorage.setItem("wk-migrated-completions", "1");
  } catch {
    // Non-fatal — never block sign-in on migration errors.
  }
}

export const Route = createFileRoute("/auth/callback")({
  component: AuthCallback,
});

function AuthCallback() {
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const SUCCESS = "/courses/business-and-accountancy/basic-business-accountancy/lessons/1.1";
    const go = (to: string) => {
      if (cancelled) return;
      window.location.replace(to);
    };
    const goSuccess = async () => {
      // Best-effort: migrate any localStorage completions to the server now
      // that we have a session. Capped at ~3s so a slow request never
      // blocks the redirect.
      await Promise.race([
        runOneTimeCompletionMigration(),
        new Promise<void>((r) => setTimeout(r, 3000)),
      ]);
      go(SUCCESS);
    };
    const fail = (reason: string) => {
      console.error("[auth/callback] failed:", reason);
      go(`/auth/error?reason=${encodeURIComponent(reason)}`);
    };
    const goIfSessionExists = async () => {
      for (let i = 0; i < 30; i++) {
        const { data } = await authClient.auth.getSession();
        if (data.session) {
          await goSuccess();
          return true;
        }
        await new Promise((r) => setTimeout(r, 100));
      }
      return false;
    };

    // SYNCHRONOUS capture + URL strip BEFORE any await, so the default
    // supabase client (lazy Proxy in client.ts, flowType: 'implicit',
    // detectSessionInUrl: true) cannot race authClient's PKCE exchange
    // by reading ?code= itself. See B-2 backlog note.
    const url = new URL(window.location.href);
    const hashParams = new URLSearchParams(
      url.hash.startsWith("#") ? url.hash.slice(1) : url.hash,
    );
    const errParam =
      url.searchParams.get("error_description") ||
      url.searchParams.get("error") ||
      hashParams.get("error_description") ||
      hashParams.get("error");
    const code = url.searchParams.get("code");
    const tokenHash = url.searchParams.get("token_hash");
    const type = url.searchParams.get("type");
    const hasImplicit = hashParams.get("access_token");

    // Strip auth params from the URL bar immediately. This must happen
    // before any await/import that could instantiate the default client.
    try {
      window.history.replaceState({}, "", "/auth/callback");
    } catch {
      // ignore — replaceState failure shouldn't block the flow
    }

    (async () => {
      try {
        if (errParam) return fail(errParam);

        // PKCE flow (default for modern Supabase magic links): ?code=...
        if (code) {
          const { error } = await authClient.auth.exchangeCodeForSession(code);
          if (error) return fail(error.message);
          return goSuccess();
        }

        // Email OTP / verify flow: ?token_hash=...&type=...
        if (tokenHash && type) {
          const { error } = await authClient.auth.verifyOtp({
            token_hash: tokenHash,
            type: type as "magiclink" | "signup" | "recovery" | "email",
          });
          if (error) return fail(error.message);
          return goSuccess();
        }

        // Implicit flow: #access_token=... — captured synchronously above.
        if (hasImplicit) {
          if (await goIfSessionExists()) return;
          return fail("Session did not materialize from implicit flow hash.");
        }

        // Supabase can consume the implicit-flow hash during client startup before
        // this effect reads window.location. If that happened, the URL is clean but
        // the session is already available and the callback should still succeed.
        if (await goIfSessionExists()) return;

        return fail("No auth token, code, or hash found in callback URL.");
      } catch (e) {
        return fail(e instanceof Error ? e.message : "Unknown callback exception.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper text-ink">
      <p style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: 18, fontStyle: "italic" }}>
        Signing you in…
      </p>
    </div>
  );
}
